/**
 * Generic offline layer for editable documents.
 *
 * Checklists got their own bespoke implementation first; everything
 * since (test reports, damage reports, tasks — and anything added
 * later) goes through here. A new document kind only needs an entry in
 * `DOC_KINDS` plus four call-sites: `loadDocs`, `createDoc`, `saveDoc`
 * and (if it can be signed) `signDoc`.
 *
 * Every helper takes the ONLINE call as a callback: online stays the
 * happy path and behaves exactly as it did before, and the device is
 * used when the browser says it's offline, when the request dies
 * mid-flight, or when the document itself hasn't reached the server yet.
 */
import {
  enqueue, cacheDocRaw, getCachedDocRaw, listCachedDocsRaw, removeCachedDocRaw,
  isLocalId, LOCAL_ID_PREFIX,
} from './offlineQueue';

const API_BASE = process.env.REACT_APP_BACKEND_URL;

export const DOC_KINDS = {
  test_report: {
    label: 'Test report',
    path: '/api/test-reports/instances',
    signPath: (id) => `/api/test-reports/instances/${id}/sign`,
    numberField: 'report_number',
    pendingNumber: 'TR-PENDING',
  },
  damage_report: {
    label: 'Damage report',
    path: '/api/project-damage-reports',
    signPath: (id) => `/api/project-damage-reports/${id}/sign-off`,
    numberField: 'report_number',
    pendingNumber: 'DR-PENDING',
  },
  task: {
    label: 'Task',
    path: '/api/project-tasks',
    signPath: null,
    numberField: null,
    pendingNumber: null,
  },
};

export const isOnlineNow = () => typeof navigator === 'undefined' || navigator.onLine !== false;

/** True for "the request never reached the server", false for a refusal. */
export const isNetworkError = (e) => (
  e instanceof TypeError
  || e?.message === 'Failed to fetch'
  || e?.code === 'ERR_NETWORK'
  || e?.message === 'Network Error'
  || (!!e?.request && !e?.response)
);

export const newLocalId = () => `${LOCAL_ID_PREFIX}${
  (typeof crypto !== 'undefined' && crypto.randomUUID) ? crypto.randomUUID() : Date.now()
}`;

export { isLocalId };

const authToken = (token) => token || (
  typeof localStorage === 'undefined' ? null : localStorage.getItem('token')
);

export async function cacheDocs(kind, rows, template = null) {
  for (const row of rows || []) {
    await cacheDocRaw(kind, row, template).catch(() => { /* quota */ });
  }
}

export { getCachedDocRaw as getCachedDoc, removeCachedDocRaw as removeCachedDoc };

/**
 * Fetch a list, keeping a copy on the device. Offline (or when the
 * fetch dies) it serves the cached rows instead. Documents created
 * offline are prepended so they don't vanish from the list while they
 * wait for signal.
 */
export async function loadDocs(kind, projectId, fetcher) {
  const cached = async () => (await listCachedDocsRaw(kind, projectId)).map((r) => r.doc);
  if (isOnlineNow()) {
    try {
      const rows = (await fetcher()) || [];
      await cacheDocs(kind, rows);
      const locals = (await listCachedDocsRaw(kind, projectId))
        .filter((r) => isLocalId(r.id))
        .map((r) => r.doc);
      return { rows: [...locals, ...rows], offline: false };
    } catch (e) {
      if (!isNetworkError(e)) throw e;
      return { rows: await cached(), offline: true };
    }
  }
  return { rows: await cached(), offline: true };
}

/** Create a document — on the server when possible, on the device otherwise. */
export async function createDoc({
  kind, projectId, payload, docShell, template, label, token, onlineCreate,
}) {
  const meta = DOC_KINDS[kind];
  if (isOnlineNow()) {
    try {
      const data = await onlineCreate();
      await cacheDocRaw(kind, data, template).catch(() => {});
      return { online: true, data };
    } catch (e) {
      if (!isNetworkError(e)) throw e;
    }
  }
  const id = newLocalId();
  const now = new Date().toISOString();
  const createPayload = { ...payload, project_id: projectId || payload?.project_id || null };
  const doc = {
    status: 'draft',
    ...createPayload,
    ...(docShell || {}),
    id,
    created_at: now,
    updated_at: now,
    _offline_created: true,
    _createPayload: createPayload,
    ...(meta.numberField && meta.pendingNumber ? { [meta.numberField]: meta.pendingNumber } : {}),
  };
  await cacheDocRaw(kind, doc, template);
  await enqueue({
    url: `${API_BASE}${meta.path}`,
    method: 'POST',
    body: createPayload,
    token: authToken(token),
    dedupeKey: `${kind}:${id}`,
    label: label || `${meta.label} (new)`,
    localDocId: id,
    localDocKind: kind,
  });
  return { online: false, data: doc };
}

/** Save an edit — queued with the base timestamp so clashes are detected. */
export async function saveDoc({
  kind, id, body, doc, template, label, token, onlineSave,
}) {
  const meta = DOC_KINDS[kind];
  const pendingCreate = isLocalId(id);
  if (!pendingCreate && isOnlineNow()) {
    try {
      const data = await onlineSave();
      await cacheDocRaw(kind, { ...(doc || {}), ...(data || {}), id }, template).catch(() => {});
      return { online: true, data };
    } catch (e) {
      if (!isNetworkError(e)) throw e;
    }
  }
  const merged = { ...(doc || {}), ...body, id, updated_at: doc?.updated_at || null };
  await cacheDocRaw(kind, merged, template);
  await enqueue({
    url: pendingCreate ? `${API_BASE}${meta.path}` : `${API_BASE}${meta.path}/${id}`,
    method: pendingCreate ? 'POST' : 'PUT',
    // A document still waiting to be created must re-send its whole
    // create payload — this entry REPLACES the queued create (same
    // dedupeKey), so a partial body would lose fields.
    body: pendingCreate
      ? { ...(doc?._createPayload || {}), ...body }
      : { ...body, base_updated_at: doc?.updated_at || doc?.created_at || null },
    token: authToken(token),
    dedupeKey: `${kind}:${id}`,
    label: label || meta.label,
    ...(pendingCreate ? { localDocId: id, localDocKind: kind, postCreateBody: body } : {}),
  });
  return { online: false, data: merged };
}

/** Sign a document — queued (with the signature image) when offline. */
export async function signDoc({
  kind, id, roleKey, body, doc, template, label, token, onlineSign,
}) {
  const meta = DOC_KINDS[kind];
  if (!isLocalId(id) && isOnlineNow()) {
    try {
      return { online: true, data: await onlineSign() };
    } catch (e) {
      if (!isNetworkError(e)) throw e;
    }
  }
  await enqueue({
    url: `${API_BASE}${meta.signPath(id)}`,
    method: 'POST',
    body,
    token: authToken(token),
    dedupeKey: `sign:${kind}:${id}:${roleKey}`,
    label: label || `Sign-off — ${meta.label}`,
  });
  if (doc) await cacheDocRaw(kind, doc, template).catch(() => {});
  return { online: false };
}
